//seeds-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import { IconButton, CircularProgress, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../components/confirm-dialog";
import LoadingBackdrop from "../components/loading-backdrop";
import { UserContext, ErrorContext } from "../components/app-routes";
import Page from "../components/page";
import { Seed } from "../models/seed";
import { seedsApi } from "../services/api/seeds";
import { ActiveFilterControls } from "../components/active-filter-controls";
import mixpanel from "mixpanel-browser";
import { Link, useHistory } from "react-router-dom";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { getCustomLabel } from "../utils/custom-labels";
import { LowStockIndicator } from "../components/low-stock-alerts";

const SeedsDataGrid = React.memo(
  (props: {
    seeds: Seed[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: any) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const context = useContext(UserContext);
    const grower = context.grower;
    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Seeds List - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "name",
        headerName: "Name",
        width: 200,
        renderCell: (params) => (
          <span>
            {params.value}
            <LowStockIndicator kind="seed" id={Number(params.id)} />
          </span>
        ),
      },
      {
        field: "variety",
        headerName: "Variety",
        width: 200,
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 200,
      },
    ];

    if (!props.seeds) return null;

    return (
      <StripedDataGrid
        storageKey="seed-page-grid"
        columnLayoutVersion={1}
        columns={columns}
        rows={props.seeds.map((c) => ({
          id: c.id,
          name: c.name,
          variety: c.variety,
          notes: c.notes,
          archivedAt: c.archivedAt,
        }))}
        initialState={{
          columns: {
            columnVisibilityModel: {
              notes: true,
            },
          },
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
          pagination: {
            paginationModel: {
              page: props.initialPage ?? 0,
              pageSize: 15,
            },
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

export default function SeedsPage() {
  const [seeds, setSeeds] = useState<Seed[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seedChangedCounter, setSeedsChangedCounter] = useState(0);
  const { handleError } = useContext(ErrorContext);
  const showArchivedCheckedStorageKey = "seedsShowArchivedChecked";
  const showActiveCheckedStorageKey = "seedsShowActiveChecked";
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );
  const history = useHistory();

  useEffect(() => {
    async function getSeeds() {
      try {
        const seeds = await seedsApi.getAll();
        setSeeds(seeds);
      } catch (error) {
        handleError(error);
      }
    }
    getSeeds();
  }, [seedChangedCounter]);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleClickTrash = () => {
    setDialogOpen(true);
  };

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setDialogOpen(false);

    if (!okTrash) {
      return;
    }

    if (!selected) {
      throw new Error("Invalid state: selected");
    }

    setBusy(true);

    let trashed = 0;

    try {
      for (let i = 0; i < selected.length; i++) {
        try {
          await seedsApi.trash(selected[i]);
          trashed++;

          mixpanel.track("Delete Seed");
        } catch (error) {
          handleError(error);
        }
      }
      setSeedsChangedCounter(seedChangedCounter + trashed);
    } finally {
      setBusy(false);
    }
  };

  const getSeedFromId = (id: number): Seed => {
    const seed = seeds?.find((v) => v.id === id);

    if (!seed) {
      throw new Error("Invalid state");
    }

    return seed;
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    const seed = getSeedFromId(id as number);
    history.push(`/home/seeds/${seed.id}`);
  };

  let initialPage = getSearchParamNumber(history, "page", 0);

  const context = useContext(UserContext);
  const grower = context.grower;

  return (
    <Page title={getCustomLabel(grower ?? null, "seeds")}>
      {seeds && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/home/seeds/add"
              disabled={busy}
            >
              Add
            </Button>
            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={handleClickTrash}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <SeedsDataGrid
              seeds={seeds.filter(
                (seed) =>
                  (showActiveChecked && seed.archivedAt === null) ||
                  (showArchivedChecked && seed.archivedAt !== null),
              )}
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({
                  search: params.toString(),
                });
              }}
              onClickRow={onClickRow}
            />
          </Box>
          {selected && (
            <ConfirmDialog
              title="Trash Seeds"
              description={`Are you sure you want to trash ${
                selected.length
              } seed${selected.length > 1 ? "s" : ""}?`}
              open={dialogOpen}
              onClose={handleTrashDialogClose}
            ></ConfirmDialog>
          )}
        </>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
